import { Box, FormHelperText, IconButton, styled, ThemeProvider, useTheme } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircleOutlineOutlined, Clear } from "@mui/icons-material";
import { Context } from "../common/context";
import { createCustomTheme } from "./account/theme";
import {
    BareAxios,
    enterInput,
    getSid,
    hideGrecaptcha,
    mountGrecaptcha,
    onlyEmptyKeys,
    svgGradient,
} from "../common/utils";
import { baseApi } from "../common/constants";
import { accountInputBoxCSS, AppLoadingButton, LoginButton, SignInCard, SignInContainer } from "./subComps/loginPieces";
import { fieldRules, LiLink } from "../common/commonComps";
import { AccountInputBox, AddressLookup } from "./account/components/TableActions";
import { Span } from "./account/components/Typography";
import type React from "react";
import type { Size, AppContext, ContextValue } from "../types";

declare const grecaptcha: { execute: (key: string, options: { action: string }) => Promise<string> };

const validationSchemaSignUp = Yup.object().shape({
    firstName: Yup.string().required(`Name and Email are required`).min(2, `Name and Email are required`),
    lastName: Yup.string().required(`Name and Email are required`).min(2, `Name and Email are required`),
    email: Yup.string().email(`Please enter a valid email`).max(255).required(`Name and Email are required`),
});

const addressValidation = Yup.object().shape({
    zipcode: Yup.string().required(`Please enter your 5 digit zipcode`).length(5, `Please enter your 5 digit zipcode`),
    address1: Yup.string().min(2, `Please enter your street address`),
});

const GreyLine = styled(Box)(() => ({
    background: `#f0f0f0`,
    borderRadius: `50px`,
    margin: `0 auto 20px`,
    padding: `3px 8px`,
    display: `flex`,
}));

const validationSchemaLogIn = Yup.object().shape({
    email: Yup.string().email(`Please enter a valid email`).max(255).required(`Email is required`),
});

const LoginInputBox = styled(AccountInputBox)(accountInputBoxCSS);

const LoginAddressLookup = styled(AddressLookup)(accountInputBoxCSS);

const codeValidationSchema = Yup.string()
    .required(`Code is required`)
    .length(4, `The code is not valid`)
    .matches(/^[A-Z]+$/, `The code is not valid`);

const Login = ({ size, context, mode }: { size: Size; context: AppContext; mode: "login" | "signup" }) => {
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [codeSent, setCodeSent] = useState<string | false>(false);
    const [email, setEmail] = useState(``);
    const [name, setName] = useState<Record<string, string>>({});
    const [expiredTimeout, setExpiredTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [wasGoogleFilled, setWasGoogleFilled] = useState(false);
    const [gettingZip, setGettingZip] = useState<boolean | number>(false);
    const [buttonSent, setButtonSent] = useState<number | boolean>(false);
    const [enterCode, setEnterCode] = useState(false);
    const [address, setAddress] = useState<Record<string, string>>({});
    const [hideZip, setHideZip] = useState(false);
    const [code, setCode] = useState(``);
    const [newUser, setNewUser] = useState<boolean | null>(null);
    const [bc, setBc] = useState<BroadcastChannel | null>(null);
    const [focus, setFocus] = useState(false);
    const [lookupOn, setLookupOn] = useState(false);
    const [shrink, setShrink] = useState(false);
    const [shouldRender, setShouldRender] = useState<boolean | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const isIframe = useMemo(() => window.self !== window.top, []);

    const handleMessage = (m: MessageEvent) => {
        try {
            // bc is initialized in the mount effect before this handler is bound
            if (m.data === `code page loaded`) {
                (bc as BroadcastChannel).postMessage(newUser ? `new user` : `old user`);
            }
            window.close();
        } catch (e) {
            (() => {})();
        }
        navigate(newUser ? `/register` : `/`);
    };

    const resetAll = () => {
        setError(null);
        setErrors({});
        setLoading(false);
        setCodeSent(false);
        setName({});
        setWasGoogleFilled(false);
        setGettingZip(false);
        setButtonSent(false);
        setAddress({});
        setCode(``);
        setNewUser(null);
        setFocus(false);
        setHideZip(false);
        setLookupOn(false);
        setFocus(false);
        if (!email) {
            setShrink(false);
        }
        clearTimeout(expiredTimeout ?? undefined);
        setExpiredTimeout(null);
    };

    const send = () => {
        clearTimeout(expiredTimeout ?? undefined);
        setExpiredTimeout(null);
        (
            (codeSent
                ? codeValidationSchema
                : mode === `signup`
                  ? validationSchemaSignUp
                  : validationSchemaLogIn
            ).validate(codeSent ? code : { ...name, email }) as Promise<unknown>
        )
            .then((): unknown =>
                mode === `signup` && (address.address1 || address.zipcode) ? addressValidation.validate(address) : true
            )
            .then(() => {
                setError(null);
                setLoading(true);
                return grecaptcha
                    .execute(`6LesMmYbAAAAACONR1kp2flqwlel9cK6gJXDvbkx`, { action: `login` })
                    .then(recaptchaToken =>
                        BareAxios.post(`${baseApi}signin`, {
                            recaptchaToken,
                            isIframe,
                            programGuid: context.programGuid,
                            ...(!codeSent
                                ? { email, ...(mode === `signup` ? { name, address } : {}) }
                                : { data: codeSent }),
                            code,
                        }).then(r => {
                            if (mode === `signup`) {
                                localStorage.setItem(`signupemail`, email);
                            }
                            if (!codeSent) {
                                setCodeSent(r.data.code);
                                setNewUser(r.data.newUser);
                                setCode(``);
                                setLoading(false);
                                setAddress({});
                                setName({});
                                setShrink(false);
                                setExpiredTimeout(
                                    setTimeout(
                                        () => {
                                            resetAll();
                                            setError(`Login Expired. Please try again.`);
                                        },
                                        1000 * 15 * 60
                                    )
                                );
                            } else {
                                hideGrecaptcha();
                                localStorage.setItem(`sid`, r.data.sid);
                                setLoading(false);
                                navigate(newUser ? `/register` : `/`);
                            }
                        })
                    );
            })
            .catch(e => {
                setButtonSent(false);
                setError(e?.errors?.[0] || e?.response?.data || `Sorry, something went wrong. Please try again.`);
                setLoading(false);
            });
    };

    const lookupZipcode = (zipcode: string) => {
        if (wasGoogleFilled) {
            return;
        }
        setGettingZip(Date.now());
        BareAxios.get(`${baseApi}cslookup?zip=${zipcode}`)
            .then(r => r.data)
            .then(r => {
                if (r.state && r.city) {
                    setAddress({ ...address, state: r.state, city: r.city, zipcode });
                } else {
                    setError(`Please enter a valid zipcode`);
                }
                setButtonSent(false);
                setGettingZip(false);
            });
    };

    useEffect(() => {
        resetAll();
    }, [location.pathname]);

    useEffect(() => {
        if (location.state?.reason === `expired`) {
            setError(`Login Expired. Please try again.`);
        }
    }, [location.state]);

    useEffect(() => {
        sessionStorage.clear();
        mountGrecaptcha();
        if (isIframe && getSid()) {
            setShouldRender(false);
        } else {
            setShouldRender(true);
        }
        return () => hideGrecaptcha();
    }, []);

    useEffect(() => {
        if (buttonSent && !gettingZip) {
            send();
        }
    }, [gettingZip, buttonSent]);

    useEffect(() => {
        const addressString = `, ${address.city || ``} ${address.state || ``}`.replace(` ,`, `,`);

        console.log(`addressString: `, `"${addressString}"`);
        if (address?.address1?.length && address.city && address.state && address.address1.includes(addressString)) {
            setAddress({ ...address, address1: address.address1.replace(addressString, ``) });
        }
    }, [address]);

    useEffect(() => {
        let channel: BroadcastChannel | undefined;
        if (!bc) {
            channel = new BroadcastChannel(`li_login`);
            setBc(channel);
        }
        if ([`/signup`, `/login`].includes(window.location.pathname) && getSid()) {
            if (!isIframe) {
                navigate(`/`);
            }
        }
        // one of bc/channel is always defined at this point
        ((bc || channel) as BroadcastChannel).onmessage = handleMessage;
    }, [newUser]);
    const raiseLabels = () => {
        setShrink(true);
    };

    const lowerLabels = () => {
        if (onlyEmptyKeys(name) && onlyEmptyKeys(address) && (!email || codeSent)) {
            setShrink(false);
        }
    };

    console.log(`address info: `, name, address, email);
    return (
        <Context.Consumer>
            {(value: ContextValue) =>
                !value.context?.adminConfig ? null : (
                    <ThemeProvider theme={createCustomTheme({ getValue: value.getValue })}>
                        {shouldRender && (
                            <SignInContainer size={size} direction="column" justifyContent="space-between">
                                <Box
                                    sx={{
                                        margin: `0 auto ${size === `small` ? 0 : `24px`}`,
                                    }}
                                >
                                    <Box
                                        sx={{ height: `auto`, width: `150px` }}
                                        component="img"
                                        src={value.context?.adminConfig?.mall?.Color_Desktop_Logo}
                                    />
                                </Box>
                                {!value.context?.adminConfig?.mall?.enable_login_page && (
                                    <Typography
                                        component="h1"
                                        variant="h4"
                                        sx={{
                                            fontFamily: `Plus Jakarta Sans`,
                                            width: `100%`,
                                            fontSize: `36px`,
                                            fontWeight: 700,
                                            textAlign: `center`,
                                        }}
                                    >
                                        This function is disabled
                                    </Typography>
                                )}
                                {value.context?.adminConfig?.mall?.enable_login_page && (
                                    <>
                                        <SignInCard
                                            size={size}
                                            wide={mode === `signup` && !codeSent}
                                            variant="outlined"
                                        >
                                            {codeSent && !enterCode && (
                                                <GreyLine>
                                                    {svgGradient(theme)}
                                                    <CheckCircleOutlineOutlined
                                                        fill="multiStopGradient"
                                                        sx={{
                                                            height: 23,
                                                            width: 23,
                                                            alignSelf: `center`,
                                                            fill: `url(#multiStopGradient)`,
                                                        }}
                                                    />
                                                    <Span sx={{ margin: `2px 0 0 5px` }}>
                                                        Email was sent to {email}
                                                    </Span>
                                                </GreyLine>
                                            )}
                                            <Typography
                                                component="h1"
                                                variant="h4"
                                                sx={{
                                                    fontFamily: `Plus Jakarta Sans`,
                                                    width: `100%`,
                                                    fontSize: `36px`,
                                                    fontWeight: 700,
                                                    textAlign: `center`,
                                                }}
                                            >
                                                {codeSent
                                                    ? enterCode
                                                        ? `Get Your Code`
                                                        : `Check Your Email`
                                                    : mode === `signup`
                                                      ? `Let's Get You Set Up`
                                                      : `Log In`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: `flex`,
                                                    flexDirection: `column`,
                                                    width: `100%`,
                                                    marginTop: mode === `signup` ? `24px` : `0`,
                                                    gap: 2,
                                                }}
                                            >
                                                {mode === `login` && !codeSent && (
                                                    <Typography
                                                        sx={{
                                                            textAlign: `center`,
                                                            fontSize: `18px`,
                                                            marginTop: `8px`,
                                                            marginBottom: `24px`,
                                                        }}
                                                    >
                                                        We&apos;ll send you a link that will log you in instantly
                                                    </Typography>
                                                )}
                                                {!codeSent && mode === `signup` && (
                                                    <>
                                                        <Box sx={{ display: `flex`, justifyContent: `space-between` }}>
                                                            {fieldRules.name.fields.map(e => (
                                                                <LoginInputBox
                                                                    key={e.l}
                                                                    name={e.htmlName}
                                                                    InputLabelProps={{ shrink }}
                                                                    type={e.t}
                                                                    autoComplete={e.ac}
                                                                    onFocus={raiseLabels}
                                                                    onBlur={lowerLabels}
                                                                    required
                                                                    label={e.l}
                                                                    id={e.htmlName}
                                                                    disabled={loading || !!e.disabled}
                                                                    onClick={() => setName({ ...name, [e.v]: `` })}
                                                                    fullWidth={!!codeSent}
                                                                    InputProps={{
                                                                        sx: {
                                                                            marginTop: `5px`,
                                                                            fontFamily: `Lato`,
                                                                        },
                                                                    }}
                                                                    sx={{
                                                                        "& label": {
                                                                            top: 0,
                                                                            "&.MuiInputLabel-shrink": {
                                                                                top: `6px`,
                                                                            },
                                                                        },
                                                                        mb: 0,
                                                                        maxWidth: `unset`,
                                                                        width: codeSent ? `unset` : `49%`,
                                                                        ...(e.hidden ? { display: `none` } : {}),
                                                                        input: {
                                                                            fontWeight: 500,
                                                                            paddingRight: `25px`,
                                                                        },
                                                                    }}
                                                                    currentValue={name[e.v]}
                                                                    helperText={errors?.[e.v]}
                                                                    error={!!errors?.[e.v]}
                                                                    onChange={(
                                                                        v: React.ChangeEvent<HTMLInputElement>
                                                                    ) => {
                                                                        setName({
                                                                            ...name,
                                                                            [e.v]: v.target.value,
                                                                        });
                                                                        setErrors({});
                                                                        setError(null);
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                overflow: `hidden`,
                                                                transition: `height .8s ease`,
                                                            }}
                                                        >
                                                            {fieldRules.address.fields.map(e => (
                                                                <FormControl
                                                                    key={e.v}
                                                                    sx={{
                                                                        "& .MuiFormControl-root": {
                                                                            marginBottom:
                                                                                e.v === `address1` ? `16px` : 0,
                                                                        },

                                                                        width: `100%`,
                                                                        mr: e.v === `address2` ? 1 : e.mr,
                                                                        maxWidth:
                                                                            e.v === `address2`
                                                                                ? `calc(100% - ${
                                                                                      (fieldRules.address.fields.find(
                                                                                          a => a.l === `Zip`
                                                                                      )!.width as number) + 8
                                                                                  }px);`
                                                                                : e.width,
                                                                        "& .MuiFormLabel-root": {
                                                                            color: `#666`,
                                                                        },
                                                                        ...([`city`, `state`].includes(e.v) ||
                                                                        (e.v === `zipcode` && hideZip)
                                                                            ? {
                                                                                  visibility: `hidden`,
                                                                                  height: 0,
                                                                                  width: `0 !important`,
                                                                                  margin: 0,
                                                                                  overflow: `hidden`,
                                                                              }
                                                                            : {}),
                                                                    }}
                                                                >
                                                                    {e.v === `address1` ? (
                                                                        <Box sx={{ position: `relative` }}>
                                                                            <LoginAddressLookup
                                                                                name={e.htmlName}
                                                                                type={e.t}
                                                                                {...({ autocomplete: e.ac } as {
                                                                                    autocomplete?: string;
                                                                                })}
                                                                                InputLabelProps={{ shrink }}
                                                                                onFocus={() => {
                                                                                    raiseLabels();
                                                                                    setFocus(true);
                                                                                }}
                                                                                onBlur={() => {
                                                                                    setFocus(false);
                                                                                    lowerLabels();
                                                                                }}
                                                                                label={e.l}
                                                                                sendResponse={v => {
                                                                                    // AddressResponse fields are all strings at runtime
                                                                                    setAddress({ ...v } as Record<
                                                                                        string,
                                                                                        string
                                                                                    >);
                                                                                    setWasGoogleFilled(true);

                                                                                    (
                                                                                        document.activeElement as HTMLElement
                                                                                    ).blur();
                                                                                }}
                                                                                id={e.htmlName}
                                                                                disabled={loading || !!e.disabled}
                                                                                fullWidth
                                                                                InputProps={{
                                                                                    endAdornment: address?.address1 && (
                                                                                        <Box>
                                                                                            <IconButton
                                                                                                tabIndex={-9999}
                                                                                                onClick={() => {
                                                                                                    setAddress({});

                                                                                                    setLookupOn(false);
                                                                                                }}
                                                                                                sx={{
                                                                                                    position: `absolute`,
                                                                                                    top: 4,
                                                                                                }}
                                                                                            >
                                                                                                <Clear
                                                                                                    sx={{
                                                                                                        opacity: 0.55,
                                                                                                    }}
                                                                                                />
                                                                                            </IconButton>

                                                                                            <Box
                                                                                                onClick={() => {
                                                                                                    setFocus(true);
                                                                                                    (
                                                                                                        document.querySelector(
                                                                                                            `#address`
                                                                                                        ) as HTMLElement
                                                                                                    ).focus();
                                                                                                }}
                                                                                                sx={{
                                                                                                    position: `absolute`,
                                                                                                    padding: `0 0 10px 15px`,
                                                                                                    top: `10px`,
                                                                                                    left: 0,
                                                                                                    display:
                                                                                                        focus ||
                                                                                                        !address?.address1
                                                                                                            ? `none`
                                                                                                            : `block`,
                                                                                                    height: `25px`,
                                                                                                    cursor: `text`,
                                                                                                    width: `calc(100% - 30px)`,
                                                                                                    overflow: `hidden`,
                                                                                                    textOverflow: `ellipsis`,
                                                                                                    whiteSpace: `nowrap`,
                                                                                                    color: loading
                                                                                                        ? `#00000061`
                                                                                                        : `#333`,
                                                                                                    background: `#F5F4F2`,
                                                                                                    zIndex: 100,
                                                                                                }}
                                                                                            >
                                                                                                {`${
                                                                                                    address[e.v] || ``
                                                                                                }, ${
                                                                                                    address.city || ``
                                                                                                } ${
                                                                                                    address.state || ``
                                                                                                }`
                                                                                                    .replace(` ,`, `,`)
                                                                                                    .trim() ||
                                                                                                    (!shrink
                                                                                                        ? `Street Address`
                                                                                                        : ``)}
                                                                                            </Box>
                                                                                        </Box>
                                                                                    ),

                                                                                    sx: {
                                                                                        marginTop: `5px`,
                                                                                        fontWeight: 500,
                                                                                        fontFamily: `Lato`,
                                                                                        ...(focus
                                                                                            ? { color: `#000000de` }
                                                                                            : { color: `#F5F4F2` }),
                                                                                    },
                                                                                }}
                                                                                sx={{
                                                                                    "& label": {
                                                                                        top: 0,
                                                                                        "&.MuiInputLabel-shrink": {
                                                                                            top: `6px`,
                                                                                        },
                                                                                    },
                                                                                    "& .MuiInputBase-root": {
                                                                                        paddingRight: `0 !important`,
                                                                                    },
                                                                                    maxWidth: `unset`,
                                                                                    ...(e.hidden
                                                                                        ? { display: `none` }
                                                                                        : {}),
                                                                                    input: { fontWeight: 500 },
                                                                                }}
                                                                                resetDirty
                                                                                lookupOn={lookupOn}
                                                                                currentValue={address[e.v]}
                                                                                helperText={errors[e.v]}
                                                                                error={!!errors[e.v]}
                                                                                onChange={(
                                                                                    v: React.ChangeEvent<HTMLInputElement>
                                                                                ) => {
                                                                                    setErrors({});
                                                                                    setError(null);
                                                                                    const val = v.target.value;
                                                                                    if (val) {
                                                                                        if (
                                                                                            Math.abs(
                                                                                                (address[e.v] ?? ``)
                                                                                                    .length -
                                                                                                    (val ?? ``).length
                                                                                            ) < 3
                                                                                        ) {
                                                                                            setLookupOn(true);
                                                                                        }
                                                                                    } else {
                                                                                        setAddress({});
                                                                                        setLookupOn(false);
                                                                                        return;
                                                                                    }

                                                                                    setAddress({
                                                                                        ...address,
                                                                                        [e.v]: val,
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    ) : (
                                                                        <LoginInputBox
                                                                            name={e.htmlName}
                                                                            InputLabelProps={{ shrink }}
                                                                            onFocus={raiseLabels}
                                                                            onBlur={lowerLabels}
                                                                            type={e.t}
                                                                            label={e.l}
                                                                            {...({ autocomplete: e.ac } as {
                                                                                autocomplete?: string;
                                                                            })}
                                                                            id={e.htmlName}
                                                                            disabled={loading || !!e.disabled}
                                                                            onClick={() =>
                                                                                setAddress({ ...address, [e.v]: `` })
                                                                            }
                                                                            fullWidth
                                                                            InputProps={{
                                                                                sx: {
                                                                                    marginTop: `5px`,
                                                                                    fontFamily: `Lato`,
                                                                                },
                                                                            }}
                                                                            sx={{
                                                                                "& label": {
                                                                                    top: 0,
                                                                                    "&.MuiInputLabel-shrink": {
                                                                                        top: `6px`,
                                                                                    },
                                                                                },

                                                                                maxWidth: `unset`,
                                                                                ...(e.hidden
                                                                                    ? { display: `none` }
                                                                                    : {}),
                                                                                input: { fontWeight: 500 },
                                                                            }}
                                                                            currentValue={address[e.v]}
                                                                            helperText={errors[e.v]}
                                                                            error={!!errors[e.v]}
                                                                            onChange={(
                                                                                v: React.ChangeEvent<HTMLInputElement>
                                                                            ) => {
                                                                                let val = v.target.value;
                                                                                if (e.v === `zipcode`) {
                                                                                    setWasGoogleFilled(false);
                                                                                    if (val.length === 5) {
                                                                                        lookupZipcode(val);
                                                                                    }
                                                                                    if (val.length > 5) {
                                                                                        val = val.slice(0, 5);
                                                                                    }
                                                                                }
                                                                                setAddress({
                                                                                    ...address,
                                                                                    [e.v]: val,
                                                                                });
                                                                                setErrors({});
                                                                                setError(null);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </FormControl>
                                                            ))}
                                                        </Box>
                                                    </>
                                                )}
                                                {codeSent && (
                                                    <Box
                                                        sx={{
                                                            display: `flex`,
                                                            flexDirection: `column`,
                                                            width: `100%`,
                                                            gap: 1,
                                                            marginTop: 1,
                                                            marginBottom: 3,
                                                        }}
                                                    >
                                                        <FormHelperText
                                                            sx={{
                                                                mt: 0,
                                                                fontSize: 18,
                                                                textAlign: `center`,
                                                                minHeight: `22px`,
                                                                minWidth: `2px`,
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {enterCode
                                                                ? `Click the link in the email you received to get your code. Then come back here and enter the code below.`
                                                                : `Open your email to log in instantly`}
                                                        </FormHelperText>
                                                        <FormHelperText
                                                            sx={{
                                                                mt: 0,
                                                                fontSize: 13,
                                                                fontWeight: 500,
                                                                minHeight: `22px`,
                                                                textAlign: `center`,
                                                                color: `#666`,
                                                            }}
                                                        >
                                                            Don&apos;t see your link? Check your Spam, Junk, or All Mail
                                                            folder.
                                                        </FormHelperText>
                                                    </Box>
                                                )}
                                                {(enterCode || !codeSent) && (
                                                    <FormControl>
                                                        <LoginInputBox
                                                            id={codeSent ? `code` : `email`}
                                                            type={codeSent ? `text` : `email`}
                                                            disabled={loading}
                                                            name={codeSent ? `code` : `email`}
                                                            onFocus={raiseLabels}
                                                            onBlur={lowerLabels}
                                                            InputLabelProps={{ shrink }}
                                                            label={codeSent ? `Enter your code here` : `Email`}
                                                            autoComplete={codeSent ? `new password` : `email`}
                                                            onKeyDown={e =>
                                                                enterInput(e, () => setButtonSent(Date.now()))
                                                            }
                                                            onChange={(v: React.ChangeEvent<HTMLInputElement>) => {
                                                                (codeSent
                                                                    ? (x: string) =>
                                                                          setCode(
                                                                              x.toUpperCase().replace(/[^A-Z]/g, ``)
                                                                          )
                                                                    : setEmail)(v.target.value);
                                                                setError(null);
                                                                setErrors({});
                                                            }}
                                                            required
                                                            fullWidth
                                                            variant="outlined"
                                                            onClick={() => setEmail(``)}
                                                            InputProps={{
                                                                sx: {
                                                                    marginTop: `5px`,
                                                                    fontFamily: `Lato`,
                                                                },
                                                            }}
                                                            sx={{
                                                                "& label": {
                                                                    top: 0,
                                                                    "&.MuiInputLabel-shrink": {
                                                                        top: `6px`,
                                                                    },
                                                                },
                                                                maxWidth: `unset`,
                                                                input: { fontWeight: 500 },
                                                            }}
                                                            currentValue={codeSent ? code : email}
                                                        />
                                                    </FormControl>
                                                )}

                                                {!!error && (
                                                    <FormHelperText
                                                        error
                                                        sx={{
                                                            mt: 1,
                                                            fontSize: 13,
                                                            fontWeight: 500,
                                                            minHeight: `22px`,
                                                            minWidth: `2px`,
                                                            textAlign: `center`,
                                                        }}
                                                    >
                                                        {error}
                                                    </FormHelperText>
                                                )}
                                                {!loading && (!codeSent || enterCode) && (
                                                    <LoginButton
                                                        sx={{
                                                            background: `linear-gradient(1deg, #007238, #00a651)`,
                                                            borderRadius: `50px`,
                                                            width: mode === `signup` ? `100%` : 150,
                                                            margin: `0 auto`,
                                                        }}
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={codeSent ? send : () => setButtonSent(Date.now())}
                                                    >
                                                        {codeSent
                                                            ? `SUBMIT`
                                                            : mode === `signup`
                                                              ? `SIGN UP`
                                                              : `Send Log In Link`}
                                                    </LoginButton>
                                                )}
                                                {loading && (
                                                    <AppLoadingButton
                                                        sx={{
                                                            background: `linear-gradient(1deg, #007238, #00a651)`,
                                                            borderRadius: `50px`,
                                                            width: mode === `signup` ? `100%` : 150,
                                                            margin: `0 auto`,
                                                        }}
                                                        spinnerColor="#FFF"
                                                        loading
                                                        variant="contained"
                                                    >
                                                        {codeSent
                                                            ? `SUBMIT`
                                                            : mode === `signup`
                                                              ? `SIGN UP`
                                                              : `Send Log In Link`}
                                                    </AppLoadingButton>
                                                )}

                                                {mode === `signup` && !codeSent && (
                                                    <Typography
                                                        sx={{ textAlign: `left`, fontSize: `13px`, marginTop: `8px` }}
                                                    >
                                                        By clicking Sign Up, you agree to our&nbsp;
                                                        <span>
                                                            <LiLink
                                                                to={
                                                                    // Terms_URL is configured when the login page is enabled
                                                                    value.context?.adminConfig?.mall?.Email_Settings
                                                                        ?.Terms_URL as string
                                                                }
                                                                target="_blank"
                                                                variant="body2"
                                                                sx={{ alignSelf: `center` }}
                                                            >
                                                                Terms
                                                            </LiLink>
                                                            &nbsp;
                                                        </span>
                                                        {value.context?.adminConfig?.mall?.Email_Settings
                                                            ?.Privacy_URL && (
                                                            <>
                                                                and acknowledge our&nbsp;
                                                                <span>
                                                                    <LiLink
                                                                        to={
                                                                            value.context?.adminConfig?.mall
                                                                                ?.Email_Settings?.Privacy_URL
                                                                        }
                                                                        target="_blank"
                                                                        variant="body2"
                                                                        sx={{ alignSelf: `center` }}
                                                                    >
                                                                        Privacy Policy
                                                                    </LiLink>
                                                                </span>
                                                            </>
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </SignInCard>
                                        {mode === `signup` && !codeSent && (
                                            <Typography
                                                sx={{ textAlign: `center`, fontSize: `13px`, marginTop: `20px` }}
                                            >
                                                Already have an account?{` `}
                                                <span>
                                                    <LiLink
                                                        to="/login"
                                                        variant="body2"
                                                        sx={{ alignSelf: `center`, fontWeight: 700 }}
                                                    >
                                                        Log In
                                                    </LiLink>
                                                </span>
                                            </Typography>
                                        )}

                                        {mode === `login` && !codeSent && (
                                            <Typography
                                                sx={{ textAlign: `center`, fontSize: `13px`, marginTop: `20px` }}
                                            >
                                                Don&apos;t have an account?{` `}
                                                <span>
                                                    <LiLink
                                                        to="/signup"
                                                        variant="body2"
                                                        sx={{ alignSelf: `center`, fontWeight: 700 }}
                                                    >
                                                        Sign up
                                                    </LiLink>
                                                </span>
                                            </Typography>
                                        )}
                                        {codeSent && !enterCode && (
                                            <Typography
                                                sx={{ textAlign: `center`, fontSize: `13px`, marginTop: `20px` }}
                                            >
                                                Did you receive a log in code?{` `}
                                                <span>
                                                    <LiLink
                                                        to=""
                                                        onClick={() => setEnterCode(true)}
                                                        variant="body2"
                                                        sx={{ alignSelf: `center`, fontWeight: 700 }}
                                                    >
                                                        click here
                                                    </LiLink>
                                                </span>
                                                {` `}
                                                to enter it.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </SignInContainer>
                        )}
                    </ThemeProvider>
                )
            }
        </Context.Consumer>
    );
};

export default Login;
